import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI, type UserInfo, type TokenResponse } from '../services/api';

interface AuthState {
  user: UserInfo | null;
  tokens: TokenResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  loginModalOpen: boolean;
  
  isAdmin: () => boolean;
  
  login: (provider: 'google' | 'yandex' | 'vk', code: string) => Promise<void>;
  emailLogin: (email: string, password: string, options?: { consent_accepted?: boolean; captcha_token?: string }) => Promise<any>;
  emailRegister: (data: { email: string; password: string; first_name?: string; last_name?: string; verification_code?: string }) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
  updateUserRole: (role: 'user' | 'editor' | 'admin') => void;
  setLoginModalOpen: (open: boolean) => void;
  acceptConsent: () => Promise<void>;
}

const initialState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  loginModalOpen: false,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      isAdmin: () => {
        const { user } = get();
        return user?.role === 'admin';
      },
      
      login: async (provider: 'google' | 'yandex' | 'vk', code: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.login(provider, code);
          set({
            user: response.user,
            tokens: response.token,
            isAuthenticated: true,
            isLoading: false,
            loginModalOpen: !response.user.agreements_accepted,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Ошибка авторизации';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      emailLogin: async (email: string, password: string, options?: { consent_accepted?: boolean; captcha_token?: string }) => {
        set({ isLoading: true });
        try {
          const response = await authAPI.emailLogin(email, password, options);
          if (response.consent_required || response.captcha_required) {
            set({ isLoading: false });
            return response;
          }
          set({
            user: response.user,
            tokens: response.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            loginModalOpen: !response.user.agreements_accepted,
          });
          return response;
        } catch (error: any) {
          const message = error.response?.data?.detail || error.message || 'Ошибка входа';
          set({ error: typeof message === 'string' ? message : 'Ошибка входа', isLoading: false });
          throw error;
        }
      },

      emailRegister: async (data: { email: string; password: string; first_name?: string; last_name?: string; verification_code?: string }) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.emailRegister(data);
          set({
            user: response.user,
            tokens: response.token,
            isAuthenticated: true,
            isLoading: false,
            loginModalOpen: false,
          });
        } catch (error: any) {
          const message = error.response?.data?.detail || error.message || 'Ошибка регистрации';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      updateUserRole: (role: 'user' | 'editor' | 'admin') => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, role } });
        }
      },
      
      logout: () => {
        authAPI.logout();
        set(initialState);
      },
      
      refreshToken: async () => {
        const { tokens } = get();
        if (!tokens?.refresh_token) {
          set({ isAuthenticated: false, user: null, tokens: null });
          return;
        }
        
        try {
          const newTokens = await authAPI.refreshToken(tokens.refresh_token);
          set({ tokens: newTokens });
        } catch (error) {
          console.error('Failed to refresh token:', error);
          set({ isAuthenticated: false, user: null, tokens: null });
        }
      },
      
      clearError: () => {
        set({ error: null });
      },
      
      checkAuth: async () => {
        const { tokens } = get();
        
        if (!tokens?.access_token) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }
        
        try {
          const user = await authAPI.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          console.error('Auth check failed:', error);
          set({ isLoading: false, isAuthenticated: false, user: null, tokens: null });
        }
      },

      setLoginModalOpen: (open: boolean) => {
        set({ loginModalOpen: open });
      },

      acceptConsent: async () => {
        try {
          await authAPI.acceptConsent();
          const { user } = get();
          if (user) {
            set({ user: { ...user, agreements_accepted: true }, loginModalOpen: false });
          }
        } catch (error) {
          console.error('Failed to accept consent:', error);
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
